import type { Identifier } from "ra-core";

import type { Email, EmailRecipient, RAFile } from "../types";

export type MailboxStatus = "disconnected" | "connected" | "error";
export type CalendarStatus = "disconnected" | "pending" | "connected" | "error";

/** A user's mailbox and calendar connection (passwords never leave the server). */
export interface MailAccount {
  id: number;
  sales_id: number;
  email: string | null;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
  mail_status: MailboxStatus;
  mail_error: string | null;
  caldav_username: string | null;
  caldav_calendar_url: string | null;
  caldav_calendar_name: string | null;
  calendar_status: CalendarStatus;
  calendar_error: string | null;
  signature_text: string | null;
  signature_image: RAFile | null;
  last_sync_at: string | null;
  history_synced_at: string | null;
}

export interface MailboxInput {
  email: string;
  /** Optional when only the servers change. */
  password?: string;
  imap_host?: string;
  imap_port?: number;
  smtp_host?: string;
  smtp_port?: number;
}

export interface CalendarInput {
  username: string;
  password: string;
}

export interface CalendarInfo {
  url: string;
  name: string;
  color?: string;
}

/** Records an email belongs to, so it shows in their feeds. */
export interface EmailLinks {
  contact_ids?: Identifier[];
  company_ids?: Identifier[];
  partner_ids?: Identifier[];
  deal_ids?: Identifier[];
}

/** An email written in the compose window. */
export interface SendEmailInput {
  to: EmailRecipient[];
  cc: EmailRecipient[];
  bcc: EmailRecipient[];
  subject: string;
  body_text: string;
  /** New files (rawFile) or files already stored (path), like a quote. */
  attachments: RAFile[];
  links: EmailLinks;
  in_reply_to?: string;
  references?: string[];
}

export type SentEmail = Email;

export type MailboxFolderKind =
  | "inbox"
  | "drafts"
  | "sent"
  | "archive"
  | "junk"
  | "trash";

/** A folder of the user's mailbox (« Messagerie » page). */
export interface MailboxFolder {
  path: string;
  name: string;
  kind: MailboxFolderKind | null;
  total: number;
  unseen: number;
}

/** A line of a folder: who, what, when. */
export interface MailboxSummary {
  uid: number;
  from: EmailRecipient | null;
  to: EmailRecipient[];
  subject: string | null;
  date: string;
  seen: boolean;
  has_attachments: boolean;
}

export interface MailboxPage {
  messages: MailboxSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface MailboxMessageRef {
  folder: string;
  uid: number;
}

export interface MailboxAttachmentInfo {
  index: number;
  filename: string;
  content_type: string;
  size: number;
}

/** A message of the mailbox, read live, with what the CRM knows of its addresses. */
export interface MailboxMessage extends MailboxMessageRef {
  message_id: string | null;
  in_reply_to: string | null;
  references: string[];
  from: EmailRecipient | null;
  to: EmailRecipient[];
  cc: EmailRecipient[];
  subject: string | null;
  date: string;
  text: string | null;
  html: string | null;
  attachments: MailboxAttachmentInfo[];
  links: Required<EmailLinks>;
  /** The email in the CRM's feeds, when it is already there. */
  crm_email_id: Identifier | null;
}

/** A file of a message, in base64. */
export interface MailboxFile {
  filename: string;
  content_type: string;
  data: string;
}

/** One event, or every coming event of an opportunity whose staff changed. */
export type CalendarSyncTarget =
  | { event_id: Identifier }
  | { deal_id: Identifier };

/** What the staff calendars got after a save. */
export interface CalendarSyncResult {
  /** The event is over: calendars are left as they are. */
  past: boolean;
  calendars: number;
  invitations: number;
  removed: number;
  errors: { partner_id: Identifier; code: string }[];
}

export interface SignatureInput {
  signature_text: string | null;
  signature_image: RAFile | null;
}

/**
 * A refused mail operation, with the code the mail-sync service sent
 * (auth_failed, unreachable, invalid_email...), translated by the page.
 */
export class MailError extends Error {
  readonly code: string;
  readonly protocol?: string;

  constructor(code: string, protocol?: string) {
    super(code);
    this.code = code;
    this.protocol = protocol;
  }
}

/** Infomaniak's servers, prefilled in the form. */
export const INFOMANIAK_DEFAULTS = {
  imap_host: "mail.infomaniak.com",
  imap_port: 993,
  smtp_host: "mail.infomaniak.com",
  smtp_port: 465,
} as const;
